import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Select, Spin, Typography, App, Tooltip } from 'antd';
import { CheckCircleFilled, CreditCardOutlined, StopOutlined } from '@ant-design/icons';
import { useHubEvent } from '../../../hooks/useHubEvent';
import {
  useCancelTerminalIntentMutation,
  useCreateTerminalIntentMutation,
  useGetMpTerminalsQuery,
  useGetTerminalIntentQuery,
  useGetMpPendingIntentQuery,
} from '../../../services/mercadoPagoApi';
import {
  useCancelGetnetIntentMutation,
  useCreateGetnetIntentMutation,
  useGetGetnetConfigQuery,
  useGetGetnetIntentQuery,
  useGetGetnetPendingIntentQuery,
} from '../../../services/getnetApi';
import type { TerminalPaymentIntent, TerminalProviderKey } from '../../../services/terminalPaymentTypes';
import { useGetSettingQuery } from '../../../services/settingsApi';
import { isOrderAlreadySplitError, ORDER_ALREADY_SPLIT_NOTICE } from '../../../utils/paymentErrors';

const { Text } = Typography;

export interface TerminalPaymentTarget {
  orderId?: string;
  splitId?: string;
  tableId?: string;
}

interface TerminalPointSectionProps {
  /** Proveedor activo del restaurante (setting TerminalProvider). */
  provider: TerminalProviderKey;
  /** Qué se cobra (orden completa o cuenta de una división). */
  target: TerminalPaymentTarget;
  /** Monto que se manda a la terminal (total con IVA, después de descuentos, sin propina). */
  amount: number;
  /**
   * Propina capturada en el POS. Solo Getnet la manda a la terminal
   * (TrxAAmount del saleWithTip); con MP el padre la bloquea vía disabledReason.
   */
  tipAmount?: number;
  /** Si viene, el botón se deshabilita y se explica por qué (pagos manuales o propina local). */
  disabledReason?: string;
  /** El webhook confirmó el cobro: el padre imprime, hace su post-pago y cierra. */
  onSettled: (intent: TerminalPaymentIntent) => void;
  /**
   * El backend rechazó el intent porque la orden ya tiene la cuenta dividida
   * (MercadoPagoPointService / GetnetPointService). Sin esto el error se queda
   * en un toast y quien cobra sigue atrapado en el modal de cobro completo,
   * que es justo lo que el backend no va a aceptar.
   */
  onOrderAlreadySplit?: () => void;
  isMobile: boolean;
}

const BRAND: Record<TerminalProviderKey, { label: string; color: string; configPath: string }> = {
  mercadopago: { label: 'Terminal Point', color: '#009ee3', configPath: 'Configuración > Terminales de cobro > Mercado Pago' },
  getnet: { label: 'Terminal Getnet', color: '#ec0000', configPath: 'Configuración > Terminales de cobro > Getnet' },
};

/**
 * Cobro con terminal bancaria (Mercado Pago Point o Getnet) dentro del
 * PaymentModal. Crea el intent del proveedor activo, muestra la espera
 * mientras el comensal paga en la terminal, y resuelve por SignalR
 * (TerminalPaymentChanged) con polling lento de fallback. El registro del pago
 * lo hace el backend al recibir el webhook — aquí nunca se llama a
 * processPayment.
 */
const TerminalPointSection: React.FC<TerminalPointSectionProps> = ({
  provider,
  target,
  amount,
  tipAmount = 0,
  disabledReason,
  onSettled,
  onOrderAlreadySplit,
  isMobile,
}) => {
  const { message } = App.useApp();
  const isMp = provider === 'mercadopago';
  const brand = BRAND[provider];
  const totalToCharge = amount + (isMp ? 0 : tipAmount);

  const [selectedTerminal, setSelectedTerminal] = useState<string>();
  const [intentId, setIntentId] = useState<string>();
  const settledRef = useRef(false);
  // Solo auto-reanudamos un cobro pendiente UNA vez al montar: si el cajero
  // luego lo cancela, no queremos volver a adoptarlo.
  const autoResumeDoneRef = useRef(false);
  // Pago confirmado: se muestra "Pago completado" un momento antes de avisar al
  // padre (que cierra el modal). Si el usuario ya se salió, el timer se limpia
  // en el unmount y no se muestra ni dispara nada — el backend ya registró el
  // pago y transicionó la mesa, la pantalla se alinea por SignalR/polling.
  const [settled, setSettled] = useState<TerminalPaymentIntent>();
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(settleTimerRef.current), []);

  // ---- Terminales disponibles por proveedor ----
  const { data: mpTerminals, isLoading: mpTerminalsLoading } = useGetMpTerminalsQuery(undefined, { skip: !isMp });
  const { data: mpDefaultSetting } = useGetSettingQuery('MercadoPagoDefaultTerminalId', { skip: !isMp });
  const { data: getnetConfig, isLoading: getnetConfigLoading } = useGetGetnetConfigQuery(undefined, { skip: isMp });

  const [createMpIntent, { isLoading: creatingMp }] = useCreateTerminalIntentMutation();
  const [cancelMpIntent, { isLoading: cancellingMp }] = useCancelTerminalIntentMutation();
  const [createGnIntent, { isLoading: creatingGn }] = useCreateGetnetIntentMutation();
  const [cancelGnIntent, { isLoading: cancellingGn }] = useCancelGetnetIntentMutation();

  const creating = creatingMp || creatingGn;
  const cancelling = cancellingMp || cancellingGn;
  const terminalsLoading = isMp ? mpTerminalsLoading : getnetConfigLoading;

  const pdvTerminals = useMemo(
    () => (mpTerminals ?? []).filter(t => t.operatingMode === 'PDV'),
    [mpTerminals],
  );

  const terminalOptions = useMemo(() => (
    isMp
      ? pdvTerminals.map(t => ({ value: t.id, label: t.id }))
      : (getnetConfig?.terminals ?? []).map(t => ({ value: t.serial, label: `${t.name} (${t.serial})` }))
  ), [isMp, pdvTerminals, getnetConfig]);

  // Preselección MP: la default configurada si está en modo PDV, o la única
  // disponible. Si la default NO aparece en la lista se usa de todos modos (la
  // terminal virtual de sandbox nunca se lista, y un fallo del listado no debe
  // bloquear el cobro); si aparece pero en STANDALONE, no — ese sí es un aviso real.
  useEffect(() => {
    if (!isMp || selectedTerminal || mpTerminalsLoading) return;
    const defaultId = mpDefaultSetting?.value;
    if (defaultId && pdvTerminals.some(t => t.id === defaultId)) {
      setSelectedTerminal(defaultId);
    } else if (pdvTerminals.length === 1) {
      setSelectedTerminal(pdvTerminals[0].id);
    } else if (defaultId && !(mpTerminals ?? []).some(t => t.id === defaultId)) {
      setSelectedTerminal(defaultId);
    }
  }, [isMp, pdvTerminals, mpTerminals, mpTerminalsLoading, mpDefaultSetting, selectedTerminal]);

  // Preselección Getnet: la default de la config, o la única dada de alta
  useEffect(() => {
    if (isMp || selectedTerminal || getnetConfigLoading || !getnetConfig) return;
    const serials = getnetConfig.terminals.map(t => t.serial);
    if (getnetConfig.defaultTerminalSerial && serials.includes(getnetConfig.defaultTerminalSerial)) {
      setSelectedTerminal(getnetConfig.defaultTerminalSerial);
    } else if (serials.length === 1) {
      setSelectedTerminal(serials[0]);
    }
  }, [isMp, getnetConfig, getnetConfigLoading, selectedTerminal]);

  // ---- Reanudar un cobro abandonado ----
  // Al montar, si esta cuenta ya tiene un intent Pending (el cajero cerró el
  // modal, el navegador se cayó, la terminal falló sin webhook), lo adoptamos
  // para mostrar "esperando" + Cancelar, en vez de crear uno nuevo y chocar
  // con el índice único ("ya hay un cobro en curso"). Solo una vez.
  const { data: mpPending } = useGetMpPendingIntentQuery(
    { orderId: target.orderId, splitId: target.splitId },
    { skip: !isMp || !!intentId || autoResumeDoneRef.current },
  );
  const { data: gnPending } = useGetGetnetPendingIntentQuery(
    { orderId: target.orderId, splitId: target.splitId },
    { skip: isMp || !!intentId || autoResumeDoneRef.current },
  );
  const pending = isMp ? mpPending : gnPending;

  useEffect(() => {
    if (autoResumeDoneRef.current || intentId || settledRef.current) return;
    if (pending === undefined) return; // aún cargando
    autoResumeDoneRef.current = true;
    if (pending && pending.status === 'Pending') {
      setIntentId(pending.id);
    }
  }, [pending, intentId]);

  // Estado del intent en curso: polling lento de fallback + refetch por SignalR
  const { data: mpIntent, refetch: refetchMp } = useGetTerminalIntentQuery(intentId!, {
    skip: !intentId || !isMp,
    pollingInterval: 5000,
  });
  const { data: gnIntent, refetch: refetchGn } = useGetGetnetIntentQuery(intentId!, {
    skip: !intentId || isMp,
    pollingInterval: 5000,
  });
  const intent = isMp ? mpIntent : gnIntent;
  const refetch = isMp ? refetchMp : refetchGn;

  useHubEvent(
    'TerminalPaymentChanged',
    (payload) => {
      if (payload?.intentId === intentId) refetch();
    },
    !!intentId,
  );

  useEffect(() => {
    if (!intent || intent.id !== intentId || settledRef.current) return;

    if (intent.status === 'Processed') {
      settledRef.current = true;
      setSettled(intent);
      settleTimerRef.current = setTimeout(() => onSettled(intent), 1800);
    } else if (intent.status === 'Failed' || intent.status === 'Expired') {
      message.error(intent.status === 'Expired'
        ? 'El cobro en la terminal expiró sin pagarse'
        : `El pago en la terminal falló${intent.statusDetail ? ` (${intent.statusDetail})` : ''}`);
      setIntentId(undefined);
    } else if (intent.status === 'Canceled') {
      setIntentId(undefined);
    }
  }, [intent, intentId, onSettled, message]);

  const handleSend = async () => {
    if (!selectedTerminal) {
      message.error('Elige la terminal a la que se manda el cobro');
      return;
    }
    try {
      settledRef.current = false;
      const body = {
        orderId: target.orderId,
        splitId: target.splitId,
        tableId: target.tableId,
        terminalId: selectedTerminal,
        tipAmount: isMp ? 0 : tipAmount,
      };
      const created = isMp
        ? await createMpIntent(body).unwrap()
        : await createGnIntent(body).unwrap();
      setIntentId(created.id);
    } catch (err) {
      if (isOrderAlreadySplitError(err) && onOrderAlreadySplit) {
        message.warning(ORDER_ALREADY_SPLIT_NOTICE);
        onOrderAlreadySplit();
        return;
      }
      message.error(
        (err as { data?: { message?: string } })?.data?.message
        ?? 'No se pudo enviar el cobro a la terminal',
      );
    }
  };

  const handleCancel = async () => {
    if (!intentId) return;
    try {
      if (isMp) {
        await cancelMpIntent(intentId).unwrap();
      } else {
        await cancelGnIntent(intentId).unwrap();
      }
      setIntentId(undefined);
      message.info('Cobro en terminal cancelado');
    } catch (err) {
      message.error(
        (err as { data?: { message?: string } })?.data?.message
        ?? 'No se pudo cancelar; si el cobro sigue en la terminal, cancélalo ahí',
      );
    }
  };

  // Pago confirmado por el webhook: confirmación breve antes de que el padre cierre
  if (settled) {
    return (
      <div
        style={{
          padding: isMobile ? 12 : 16,
          border: '1px solid var(--border-color)',
          borderRadius: 8,
          backgroundColor: 'var(--bg-primary)',
          textAlign: 'center',
        }}
      >
        <CheckCircleFilled style={{ fontSize: 32, color: '#52c41a' }} />
        <div style={{ marginTop: 8 }}>
          <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
            Pago completado
          </Text>
        </div>
        <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
          ${settled.paidAmount.toFixed(2)} cobrados en la terminal
          {settled.tipAmount > 0 ? ` (incluye $${settled.tipAmount.toFixed(2)} de propina)` : ''}
        </Text>
      </div>
    );
  }

  // Esperando el pago en la terminal
  if (intentId) {
    return (
      <div
        style={{
          padding: isMobile ? 12 : 16,
          border: '1px solid var(--border-color)',
          borderRadius: 8,
          backgroundColor: 'var(--bg-primary)',
          textAlign: 'center',
        }}
      >
        <Spin />
        <div style={{ marginTop: 8 }}>
          <Text strong style={{ fontSize: isMobile ? 13 : 14 }}>
            Esperando el pago en la terminal…
          </Text>
        </div>
        <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
          ${totalToCharge.toFixed(2)} enviados a {intent?.terminalId ?? selectedTerminal}.
          {isMp
            ? ' El comensal puede agregar la propina en la terminal.'
            : tipAmount > 0
              ? ` Incluye $${tipAmount.toFixed(2)} de propina. El comensal paga directamente en la terminal.`
              : ' El comensal paga directamente en la terminal.'}
        </Text>
        <div style={{ marginTop: 12 }}>
          <Button icon={<StopOutlined />} danger loading={cancelling} onClick={handleCancel} size="small">
            Cancelar cobro
          </Button>
        </div>
      </div>
    );
  }

  const noTerminalAvailable = !terminalsLoading && terminalOptions.length === 0 && !selectedTerminal;
  const disabled = !!disabledReason || noTerminalAvailable || amount <= 0;
  const tooltip = disabledReason
    ?? (noTerminalAvailable
      ? (isMp
        ? `No hay terminales en modo punto de venta (configúralas en ${brand.configPath})`
        : `No hay terminales dadas de alta (agrégalas en ${brand.configPath})`)
      : undefined);

  return (
    <div>
      <Text
        strong
        style={{ display: 'block', marginBottom: 8, fontSize: isMobile ? 12 : 14 }}
      >
        {brand.label}:
      </Text>
      {terminalOptions.length > 1 && (
        <Select
          value={selectedTerminal}
          onChange={setSelectedTerminal}
          style={{ width: '100%', marginBottom: 8 }}
          placeholder="Elige la terminal"
          size={isMobile ? 'middle' : 'large'}
          options={terminalOptions}
        />
      )}
      <Tooltip title={tooltip}>
        <Button
          block
          icon={<CreditCardOutlined />}
          onClick={handleSend}
          loading={creating || terminalsLoading}
          disabled={disabled}
          size={isMobile ? 'middle' : 'large'}
          style={disabled ? undefined : { borderColor: brand.color, color: brand.color }}
        >
          Cobrar ${totalToCharge.toFixed(2)} en {brand.label}
        </Button>
      </Tooltip>
      <Text type="secondary" style={{ fontSize: isMobile ? 10 : 12, display: 'block', marginTop: 4 }}>
        {isMp
          ? 'La propina se captura en la terminal y se registra automáticamente.'
          : tipAmount > 0
            ? `Incluye $${tipAmount.toFixed(2)} de propina. El pago se registra y la cuenta se cierra al aprobarse.`
            : 'El pago se registra y la cuenta se cierra automáticamente al aprobarse.'}
      </Text>
    </div>
  );
};

export default TerminalPointSection;
